import {
  ALERT_SEVERITY_ERROR,
  ALERT_SEVERITY_SUCCESS,
  ERROR_STATUS,
  ERROR_STATUS_NOT_FOUND,
  SUCCESS_STATUS,
} from "components/MessageService/constants";

import { baseUrl, headers } from "lib/api/constants";

import storeConfig from "store";
import { logout } from "store/auth/actions";
import { refreshAuth } from "store/auth/actions";

function getResponseType(status) {
  switch (status) {
    case 500:
    default:
      return {
        type: ERROR_STATUS,
        severity: ALERT_SEVERITY_ERROR,
      };
    case 400:
      return {
        type: ERROR_STATUS,
        severity: ALERT_SEVERITY_ERROR,
      };
    case 404:
      return {
        type: ERROR_STATUS_NOT_FOUND,
        severity: ALERT_SEVERITY_ERROR,
      };
    case 200:
      return {
        type: SUCCESS_STATUS,
        severity: ALERT_SEVERITY_SUCCESS,
      };
  }
}

function handleError(error) {
  throw Object.assign({
    message: error.message,
    code: 500,
    ...getResponseType(500),
  });
}

async function handleResponse(response) {
  if (response.status === 401) {
    try { storeConfig().store.dispatch(logout()); } catch (e) {}
    try {
      if (typeof window !== 'undefined' && window.location && window.location.pathname !== '/login') {
        window.location.assign('/login');
      }
    } catch (e) {}
    throw Object.assign(new Error('Unauthorized'), {
      message: 'Unauthorized',
      code: response.status,
      ...getResponseType(response.status),
    });
  }

  let jsonResponse = {};
  try {
    jsonResponse = await response.json();
  } catch (e) {
    jsonResponse = {};
  }

  const { data, message, status } = jsonResponse;
  const numericStatus = Number(status);
  const effectiveStatus = Number.isFinite(numericStatus) ? numericStatus : response.status;
  const forceError = typeof message === 'string' && /unauthorized/i.test(message);
  const statusForUi = forceError ? 500 : effectiveStatus;
  
  const exportStatus = typeof status === 'string' && !Number.isFinite(numericStatus) ? status : undefined;

  try {
    const indicativeMsg = String(message || '').toLowerCase();
    const shouldRefresh = effectiveStatus === 403 || /unauthorized|disabled|no active roles|no active clients/.test(indicativeMsg);
    if (shouldRefresh) {
      const store = storeConfig().store;
      if (store && typeof store.dispatch === 'function') {
        await store.dispatch(refreshAuth());
        const state = store.getState && store.getState();
        const ao = state?.authReducer?.authObject;
        const hasAdminRoles = !!(ao?.isAdmin && Array.isArray(ao?.roles) && ao.roles.length);
        const hasClientRoles = Array.isArray(ao?.clientRoles) && ao.clientRoles.some(cr => cr && cr.active !== false && Array.isArray(cr.roles) && cr.roles.length);
        const accountDisabled = ao && ao.active === false;
        if (accountDisabled || (!hasAdminRoles && !hasClientRoles)) {
          try { store.dispatch(logout()); } catch (e) {}
          try {
            if (typeof window !== 'undefined' && window.location && window.location.pathname !== '/login') {
              window.location.assign('/login');
            }
          } catch (e) {}
        }
      }
    }
  } catch (e) {}

  return {
    data,
    message,
    code: effectiveStatus,
    exportStatus,
    ...getResponseType(statusForUi),
  };
}

async function handleBlobResponse(response) {
  return response.blob();
}

function formatQueryParams(queryParams) {
  const stringQueryParams = new URLSearchParams(queryParams).toString();
  let params = "";
  if (stringQueryParams) params = `?${stringQueryParams}`;
  return params;
}

function getAuthObject() {
  try {
    const store = storeConfig().store;
    if (!store) {
      try {
        const persisted = localStorage.getItem('persist:root');
        if (persisted) {
          const parsed = JSON.parse(persisted);
          const authState = JSON.parse(parsed.authReducer);
          return authState.authObject;
        }
      } catch (e) {
        console.error('Error reading from localStorage:', e);
      }
      return null;
    }
    const state = store.getState();
    if (!state || !state.authReducer || !state.authReducer.authObject) {
      try {
        const persisted = localStorage.getItem('persist:root');
        if (persisted) {
          const parsed = JSON.parse(persisted);
          const authState = JSON.parse(parsed.authReducer);
          return authState.authObject;
        }
      } catch (e) {
        console.error('Error reading from localStorage:', e);
      }
      return null;
    }
    return state.authReducer.authObject;
  } catch (e) {
    console.error('Error getting authObject:', e);
    return null;
  }
}

function isTokenExpired(token) {
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    const payload = JSON.parse(atob(parts[1]));
    if (!payload || !payload.exp) return true;
    const nowMs = Date.now();
    return payload.exp * 1000 <= nowMs;
  } catch (e) {
    return true;
  }
}

function getAuthToken() {
  try {
    const auth = getAuthObject();
    if (auth && auth.token) return auth.token;
    const persisted = localStorage.getItem('persist:root');
    if (persisted) {
      const parsed = JSON.parse(persisted);
      const authState = JSON.parse(parsed.authReducer);
      return authState?.authObject?.token || null;
    }
  } catch (e) {}
  return null;
}

async function get(resource, queryParams = {}, responseType = "json") {
  const cleanParams = { ...queryParams };
  
  let requestHeaders = { ...headers };
  const token = getAuthToken();
  if (!token || isTokenExpired(token)) {
    try { storeConfig().store.dispatch(logout()); } catch (e) {}
    return Promise.reject({ message: 'Unauthorized', code: 401 });
  }
  if (token) {
    requestHeaders = { ...requestHeaders, Authorization: `Bearer ${token}` };
  }

  try {
    const urlToFetch = `${baseUrl}/${resource}${formatQueryParams(cleanParams)}`;
    return fetch(urlToFetch, {
      method: "GET",
      headers: requestHeaders,
    })
      .then(responseType === "blob" ? handleBlobResponse : handleResponse)
      .catch(handleError);
  } catch (err) {
    return handleError(err);
  }
}

function parseRequestBody(data, withUser = true) {
  return async (dispatch, getState) => {
    let payload = data;
    try {
      if (withUser) {
        const authObj = getState().authReducer.authObject;
        const normalizedRoles = (() => {
          try {
            const r = Array.isArray(authObj?.roles) ? [...authObj.roles] : [];
            if (authObj?.isAdmin && !r.includes('admin')) r.push('admin');
            return r;
          } catch (e) { return authObj?.roles; }
        })();
        payload = {
          ...payload,
          currentUser: {
            ...authObj,
            roles: normalizedRoles,
          },
        };
      }
    } catch (e) {}
    return payload;
  };
}

async function sendData(resource, data, edit = false, withUser = true) {
  const dispatch = storeConfig().store.dispatch;

  let requestHeaders = { ...headers, "Content-Type": "application/json" };
  if (withUser !== false) {
    const token = getAuthToken();
    if (!token || isTokenExpired(token)) {
      try { storeConfig().store.dispatch(logout()); } catch (e) {}
      return Promise.reject({ message: 'Unauthorized', code: 401 });
    }
    if (token) {
      requestHeaders = { ...requestHeaders, Authorization: `Bearer ${token}` };
    }
  }

  return fetch(`${baseUrl}/${resource}`, {
    method: edit ? "PUT" : "POST",
    headers: requestHeaders,
    body: JSON.stringify(await dispatch(parseRequestBody(data, withUser))),
  })
    .then(handleResponse)
    .catch(handleError);
}

function post(resource, data, withUser = true) {
  return sendData(resource, data, false, withUser);
}

function put(resource, data) {
  return sendData(resource, data, true);
}

function deleteMethodItem(resource, data) {
  let requestHeaders = { ...headers };
  const token = getAuthToken();
  if (!token || isTokenExpired(token)) {
    try { storeConfig().store.dispatch(logout()); } catch (e) {}
    return Promise.reject({ message: 'Unauthorized', code: 401 });
  }
  if (token) {
    requestHeaders = { ...requestHeaders, Authorization: `Bearer ${token}` };
  }

  return fetch(`${baseUrl}/${resource}`, {
    method: "DELETE",
    headers: requestHeaders,
    body: JSON.stringify(parseRequestBody({})),
  })
    .then(handleResponse)
    .catch(handleError);
}

function deleteMethod(resource, data) {
  let requestHeaders = { ...headers };
  const token = getAuthToken();
  if (!token || isTokenExpired(token)) {
    try { storeConfig().store.dispatch(logout()); } catch (e) {}
    return Promise.reject({ message: 'Unauthorized', code: 401 });
  }
  if (token) {
    requestHeaders = { ...requestHeaders, Authorization: `Bearer ${token}` };
  }

  return fetch(`${baseUrl}/${resource}${formatQueryParams(data)}`, {
    method: "DELETE",
    headers: requestHeaders,
    body: JSON.stringify(parseRequestBody({})),
  })
    .then(handleResponse)
    .catch(handleError);
}

export { get, post, put, deleteMethod, deleteMethodItem };
